import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import { AlertTitle } from '@mui/material';
import {
  Block as BlockIcon,
  Warning as WarningIcon,
  Email as EmailIcon,
  Gavel as GavelIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr, enUS, arSA } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import openAttachment from '../utils/attachmentUtils';
import api from '../services/api';
import BanAppealForm from '../components/user/BanAppealForm';

const styles = {
  blinkingInfo: {
    animation: 'banInfoPulse 1.2s ease-in-out infinite'
  }
};

/**
 * Page affichée aux utilisateurs dont le compte est restreint ou banni
 */
const AccountRestrictedPage = () => {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const { t, i18n } = useTranslation('accountRestricted');
  const [loading, setLoading] = useState(true);
  const [restrictionInfo, setRestrictionInfo] = useState(null);
  const [error, setError] = useState(null);
  const [showAppealForm, setShowAppealForm] = useState(false);
  const [banInfoOpen, setBanInfoOpen] = useState(false);
  const [myAppeals, setMyAppeals] = useState([]);
  const [myAppealsLoading, setMyAppealsLoading] = useState(false);

  useEffect(() => {
    fetchRestrictionInfo();
    fetchMyAppeals();
  }, []);

  const fetchRestrictionInfo = async () => {
    try {
      const response = await api.get('/users/me/restrictions');
      setRestrictionInfo(response.data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyAppeals = async () => {
    try {
      setMyAppealsLoading(true);
      const response = await api.get('/appeals/my');
      setMyAppeals(response.data.data || []);
    } catch (err) {
      // Ne pas bloquer la page si ça échoue
      console.error('Failed to load appeals:', err);
    } finally {
      setMyAppealsLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return null;
    const localeMap = { fr, en: enUS, ar: arSA };
    const locale = localeMap[i18n.language] || fr;
    return format(new Date(date), "dd/MM/yyyy HH:mm", { locale });
  };

  const translateEventType = (type) => {
    const map = {
      appeal_submitted: t('history.event_appeal_submitted'),
      appeal_reviewed: t('history.event_appeal_reviewed')
    };
    return map[type] || type;
  };

  const translateAppealStatus = (status) => {
    const map = {
      approved: t('history.status.approved'),
      rejected: t('history.status.rejected'),
      pending: t('history.status.pending')
    };
    return map[status] || status;
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const isBanned = restrictionInfo?.isBanned;
  const banType = restrictionInfo?.banType;
  const banUntil = restrictionInfo?.banExpiresAt;
  const banReason = restrictionInfo?.banReason;
  const activeRestrictions = restrictionInfo?.restrictions || [];

  const hasActiveRestrictions = activeRestrictions.length > 0;
  const isRestricted = !!isBanned || hasActiveRestrictions;

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        {/* Icône principale */}
        <Box sx={{ mb: 3 }}>
          {isBanned ? (
            <BlockIcon sx={{ fontSize: 80, color: 'error.main' }} />
          ) : (
            <WarningIcon sx={{ fontSize: 80, color: 'warning.main' }} />
          )}
        </Box>

        {/* Titre */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h4" gutterBottom color={isBanned ? 'error' : isRestricted ? 'warning.dark' : 'success.main'} sx={{ mb: 0 }}>
            {isBanned ? t('title.banned') : isRestricted ? t('title.restricted') : t('title.active')}
          </Typography>

          {isBanned && (
            <IconButton
              aria-label="Informations sur les conséquences du bannissement"
              size="small"
              onClick={() => setBanInfoOpen(true)}
              sx={{
                ...styles.blinkingInfo,
                color: 'info.main',
                border: '1px solid',
                borderColor: 'info.main'
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Message principal */}
        {isBanned ? (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {banType === 'permanent' ? (
              t('message.permanent_ban')
            ) : (
              <>
                {t('message.temporary_ban')}
                {banUntil && (
                  <strong> {t('message.until')} {formatDate(banUntil)}</strong>
                )}.
              </>
            )}
          </Typography>
        ) : isRestricted ? (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('message.restrictions_active')}
          </Typography>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('message.no_restrictions')}
          </Typography>
        )}

        {/* Raison */}
        {banReason && isBanned && (
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('labels.reason')}:
            </Typography>
            <Typography variant="body2">
              {banReason}
            </Typography>
          </Alert>
        )}

        {/* Restrictions actives (si pas banni) */}
        {!isBanned && activeRestrictions.length > 0 && (
          <Box sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('labels.active_restrictions')}:
            </Typography>
            <List dense>
              {activeRestrictions.map((restriction, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <WarningIcon color="warning" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(getRestrictionLabel(restriction.restriction_type))}
                    secondary={restriction.effective_until ? `${t('labels.expires')} ${formatDate(restriction.effective_until)}` : t('labels.permanent')}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Actions possibles */}
        {isRestricted && (
          <>
            <Typography variant="subtitle2" gutterBottom sx={{ textAlign: 'left' }}>
              {t('section.what_can_i_do')}
            </Typography>
            
            <List sx={{ textAlign: 'left' }}>
              {isBanned && (
                <ListItem>
                  <ListItemIcon>
                    <GavelIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('actions.appeal')}
                    secondary={t('actions.appeal_description')}
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon>
                  <EmailIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t('actions.contact_support')}
                  secondary={t('actions.contact_support_description')}
                />
              </ListItem>
              {banType === 'temporary' && banUntil && isBanned && (
                <ListItem>
                  <ListItemIcon>
                    <AccessTimeIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('actions.wait_for_lift')}
                    secondary={`${t('actions.access_restored')} ${formatDate(banUntil)}.`}
                  />
                </ListItem>
              )}
            </List>
          </>
        )}

        {/* Boutons */}
        <Stack spacing={2} sx={{ mt: 3 }}>
          {isBanned && !showAppealForm && (
            <Button
              variant="contained"
              startIcon={<GavelIcon />}
              onClick={() => setShowAppealForm(true)}
            >
              {t('buttons.appeal_decision')}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            href="mailto:support@krillou.com"
          >
            {t('buttons.contact_support')}
          </Button>
          <Button
            variant="text"
            color="inherit"
            onClick={logout}
          >
            {t('buttons.logout')}
          </Button>
        </Stack>

        {/* Formulaire d'appel */}
        {showAppealForm && isBanned && restrictionInfo?.activeBanId && (
          <Box sx={{ mt: 4 }}>
            <BanAppealForm
              restrictionId={restrictionInfo.activeBanId}
              onSuccess={() => {
                setShowAppealForm(false);
                fetchRestrictionInfo();
                fetchMyAppeals();
              }}
              onCancel={() => setShowAppealForm(false)}
            />
          </Box>
        )}

        {/* Historique des bans & appels (Accordéons) */}
        {(isBanned || myAppeals.length > 0) && !showAppealForm && (
          <Box sx={{ mt: 4, textAlign: 'left' }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('history.bans_and_appeals')}
              </Typography>

              {myAppealsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (myAppeals || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('history.no_history')}
                </Typography>
              ) : (
                <Box>
                  {(() => {
                    const activeBanId = restrictionInfo?.activeBanId;
                    const sorted = [...(myAppeals || [])].sort((a, b) => {
                      const da = a?.bannedAt ? new Date(a.bannedAt).getTime() : 0;
                      const db = b?.bannedAt ? new Date(b.bannedAt).getTime() : 0;
                      return db - da;
                    });

                    return sorted.map((group, idx) => {
                      const isActive = !!activeBanId && group.restrictionId === activeBanId;
                      const isMostRecent = idx === 0;

                      const appeals = Array.isArray(group.appeals) ? group.appeals : [];
                      const latestSubmission = appeals.find(a => a.type === 'appeal_submitted');

                      // Résolution (approved/rejected) : on cherche l'event appeal_submitted dont metadata.status est approved/rejected
                      const resolvedSubmission = appeals.find(a =>
                        a.type === 'appeal_submitted' &&
                        (a.metadata?.status === 'approved' || a.metadata?.status === 'rejected')
                      );

                      const decision = resolvedSubmission?.metadata?.status || null;
                      const responseMessage = resolvedSubmission?.metadata?.response_message || null;

                      const highlightSx = isActive
                        ? { borderLeft: '6px solid', borderLeftColor: 'error.main', bgcolor: 'error.50' }
                        : isMostRecent
                          ? { borderLeft: '6px solid', borderLeftColor: 'warning.main', bgcolor: 'warning.50' }
                          : { borderLeft: '6px solid', borderLeftColor: 'divider', bgcolor: 'background.paper' };

                      return (
                        <Accordion key={group.restrictionId} defaultExpanded={isActive || isMostRecent} sx={{ mb: 1, ...highlightSx }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}> 
                            <Box sx={{ width: '100%' }}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                  {group.restrictionType === 'ban_permanent' ? t('history.permanent_ban') : t('history.temporary_ban')}
                                </Typography>

                                {isActive && <Chip label={t('history.ban_active')} color="error" size="small" />}
                                {!isActive && isMostRecent && <Chip label={t('history.last_ban')} color="warning" size="small" />}

                                {decision === 'approved' && <Chip label={t('history.appeal_approved')} color="success" size="small" />}
                                {decision === 'rejected' && <Chip label={t('history.appeal_rejected')} color="error" size="small" />}
                                {latestSubmission?.metadata?.status === 'pending' && <Chip label={t('history.appeal_pending')} color="info" size="small" />}
                              </Stack>

                              <Typography variant="caption" color="text.secondary">
                                {t('history.ban_applied')} {group.bannedAt ? formatDate(group.bannedAt) : 'N/A'}
                              </Typography>
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails>
                            {group.banReason && (
                              <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="subtitle2">{t('history.ban_reason_title')}</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{group.banReason}</Typography>
                              </Alert>
                            )}

                            {decision === 'rejected' && (
                              <Alert severity="error" sx={{ mb: 2 }}>
                                <AlertTitle>{t('history.appeal_rejected_alert')}</AlertTitle>
                                {responseMessage ? (
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{responseMessage}</Typography>
                                ) : (
                                  <Typography variant="body2">{t('history.no_response_message')}</Typography>
                                )}
                              </Alert>
                            )}

                            {decision === 'approved' && (
                              <Alert severity="success" sx={{ mb: 2 }}>
                                <AlertTitle>{t('history.appeal_approved_alert')}</AlertTitle>
                                {responseMessage ? (
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{responseMessage}</Typography>
                                ) : (
                                  <Typography variant="body2">{t('history.appeal_accepted')}</Typography>
                                )}
                              </Alert>
                            )}

                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              {t('history.exchange_history')}
                            </Typography>

                            {appeals.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">{t('history.no_events')}</Typography>
                            ) : (
                              <List dense>
                                {appeals.map((a) => (
                                  <Box key={a.id} sx={{ mb: 2, pl: 1, borderLeft: '3px solid', borderColor: 'divider' }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {translateEventType(a.type)} — {a.createdAt ? formatDate(a.createdAt) : 'N/A'}
                                    </Typography>

                                    {a.message && (
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                                        {a.message}
                                      </Typography>
                                    )}

                                    {(a.type === 'appeal_submitted') && a.metadata?.status && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        {t('history.appeal_status')}: {translateAppealStatus(a.metadata.status)}
                                      </Typography>
                                    )}

                                    {(a.attachments || []).length > 0 && (
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          {t('history.attachments')}:
                                        </Typography>
                                        <List dense>
                                          {a.attachments.map((att) => (
                                            <ListItem key={att.id} sx={{ py: 0.25 }}>
                                              <ListItemIcon>
                                                <InfoIcon fontSize="small" />
                                              </ListItemIcon>
                                              <ListItemText
                                                primary={att.originalName || att.filename}
                                                secondary={att.mimeType || ''}
                                              />
                                              <Button
                                                size="small"
                                                onClick={() => openAttachment(a.id, att.id, token).catch(err => console.error(err))}
                                              >
                                                {t('buttons.view')}
                                              </Button>
                                            </ListItem>
                                          ))}
                                        </List>
                                      </Box>
                                    )}
                                  </Box>
                                ))}
                              </List>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    });
                  })()}
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {/* Carte des strikes (affiche les infos depuis l'API utilisateur) */}
        {restrictionInfo?.strikes && (
          <Box sx={{ mt: 4, textAlign: 'left' }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('penalties.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('penalties.active_points')}: <strong>{restrictionInfo.strikes.currentPoints}</strong> / {restrictionInfo.strikes.maxPoints}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('penalties.strikes_label')}: <strong>{restrictionInfo.strikes.activeStrikes}</strong>
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Info légale */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            {t('legal.rights_notice')}{' '}
            <a href="/legal/terms" target="_blank" rel="noopener">
              {t('legal.terms_link')}
            </a>
            .
          </Typography>
        </Box>
      {/* Modal info ban */}
        <Dialog open={banInfoOpen} onClose={() => setBanInfoOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('modal.ban_consequences_title')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('modal.ban_consequences_desc')}
            </Typography>

            <List dense>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon color="info" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('modal.listing_creation')}
                  secondary={t('modal.listing_creation_desc')}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon color="info" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('modal.existing_listings')}
                  secondary={t('modal.existing_listings_desc')}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon color="info" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('modal.payouts')}
                  secondary={t('modal.payouts_desc')}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon color="info" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('modal.reservations')}
                  secondary={t('modal.reservations_desc')}
                />
              </ListItem>
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBanInfoOpen(false)} variant="contained">{t('buttons.close')}</Button>
          </DialogActions>
        </Dialog>

        <style>{`@keyframes banInfoPulse { 0% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.15); opacity: 0.6; } 100% { transform: scale(1); opacity: 1; } }`}</style>
      </Paper>
    </Container>
  );
};

/**
 * Labels des types de restrictions
 */
function getRestrictionLabel(type) {
  // Utiliser le namespace 'accountRestricted' pour obtenir les labels de restrictions
  // Les labels seront récupérés via t('restrictionLabels.xxx')
  const labels = {
    booking_block: 'restrictionLabels.booking_block',
    listing_unpublish: 'restrictionLabels.listing_unpublish',
    message_block: 'restrictionLabels.message_block',
    payout_hold: 'restrictionLabels.payout_hold',
    payin_block: 'restrictionLabels.payin_block',
    kyc_hold: 'restrictionLabels.kyc_hold'
  };
  return labels[type] || type;
}

export default AccountRestrictedPage;
